import React, { useEffect, useState } from "react";
import { formatClock, formatDay, formatDayTime, initialsName, mergeMissingResultRow } from "../api/shape.js";
import { validatePeriodInput } from "../api/reportPeriod.js";
import { reportRef } from "../api/reportLabel.js";
import { itemsToFix } from "../api/reportSteps.js";
import { LINE_LABEL, PUBLISH_CHECKS, STATUS_TEXT, outcomeText, unapprovedRecordsText } from "../screens/report/text.js";
import { useEarlierVersions } from "../screens/report/ReportsTable.jsx";
import { PhoneChip, PhoneFullScreen, PhoneSheet, Svg, ICON } from "./PhoneShell.jsx";
import { flowScreenKind, flowScreenSignature, nextStepCard, reportCardActions, reportCardMeta, reportScreenTitle } from "./reportPhone.js";

const STATUS_TONE = { draft: "warn", approved: "acc", published: "ok", legacy: "mute" };

/** The file name at the end of a link ("" when there is none). */
function fileNameOf(url) {
  if (!url) return "";
  try {
    return decodeURIComponent(String(url).split("?")[0].split("/").pop() || "");
  } catch {
    return "";
  }
}

function ExternalLink({ url, className, children, ...rest }) {
  return url ? (
    <a className={className} href={url} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>
  ) : (
    <span className={className} aria-disabled="true" {...rest}>{children}</span>
  );
}

/** The next-step card: ONE primary action for the current step (reportSteps.js), a progress bar, the step's own help text. */
function NextStepCard({ card, onAction, busy }) {
  return (
    <section aria-label="Next step" className="ph-stepcard" data-testid="ph-next-step">
      <span className="ph-stepno">{card.done ? "Done" : `Step ${card.no} of ${card.total}`}</span>
      <h2 className="ph-steptitle">{card.title}</h2>
      {card.text ? <p className="ph-steptext">{card.text}</p> : null}
      {card.primary ? (
        <button type="button" className="ph-btn pri" data-testid="ph-next-primary" disabled={card.primary.locked || busy} onClick={() => onAction(card.primary.action)}>
          {card.primary.label}
        </button>
      ) : null}
      {card.lockNote ? <span className="ph-small" style={{ color: "var(--ph-warn)" }}>{card.lockNote}</span> : null}
      {card.secondary ? <ExternalLink url={card.secondary.url} className="ph-primary-text" style={{ textDecoration: "none" }}>{card.secondary.label}</ExternalLink> : null}
      <ol className="ph-progress" aria-label="Progress">
        {card.bar.map((b, i) => (
          <li key={i} className={b.state === "done" ? "done" : b.state === "current" ? "now" : ""}><span>{b.label}</span></li>
        ))}
      </ol>
    </section>
  );
}

/** ReportPublished.dc.html "Earlier versions": the superseded versions of the published report, opened read only. */
function EarlierVersions({ row, onLoadVersions }) {
  const history = useEarlierVersions(row, onLoadVersions);
  // The published screen lists them at once, as the design does.
  useEffect(() => {
    if (row.hasHistory) history.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.reportNumber]);
  if (!row.hasHistory) return null;
  return (
    <section className="ph-section" data-testid={`report-history-${row.reportNumber}`}>
      <h2 className="ph-label">Earlier versions · {row.historyCount}</h2>
      {history.loading ? <span className="ph-note">Loading…</span> : null}
      {history.links.map((link) => (
        <div key={link.key} className="ph-versionrow">
          <span>{link.label} · superseded</span>
          {link.url ? <a href={link.url} target="_blank" rel="noopener noreferrer">Open read only</a> : <span className="ph-faint">no link</span>}
        </div>
      ))}
    </section>
  );
}

/** One report as a card instead of a table row: the same actions the row shows. */
function ReportCard({ row, current, busy, canPublish, canRetry, onApprove, onRetry, onPublish, open, onToggle, onLoadVersions }) {
  const history = useEarlierVersions(row, onLoadVersions);
  const actions = reportCardActions(row, { current, canPublish, canRetry });
  return (
    <article className={`ph-reportcard${current ? " now" : ""}`} data-testid={`report-row-${row.reportNumber}`}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span className="ph-cardtitle">{row.reportText}</span>
        <PhoneChip tone={STATUS_TONE[row.statusTone] || "mute"} className={`ph-chip big ${STATUS_TONE[row.statusTone] || "mute"}`}>{row.statusText}</PhoneChip>
      </div>
      <span className="ph-mono ph-small">{reportCardMeta(row)}</span>
      <div className="ph-two">
        {actions.map((a) => {
          if (a.kind === "word") {
            return <ExternalLink key={a.kind} url={a.url} className="ph-btn" data-testid={`report-word-${row.reportNumber}`}>{a.label}</ExternalLink>;
          }
          if (a.kind === "approve") return <button key={a.kind} type="button" className="ph-btn sec" data-testid={`report-approve-${row.reportNumber}`} disabled={busy} onClick={() => onApprove(row)}>{a.label}</button>;
          if (a.kind === "retry") return <button key={a.kind} type="button" className="ph-btn warn" data-testid="report-pdf-retry" disabled={busy || a.disabled} onClick={() => onRetry(row)}>{a.label}</button>;
          if (a.kind === "site") return <ExternalLink key={a.kind} url={a.url} className="ph-btn ok" data-testid={`report-open-site-${row.reportNumber}`}>{a.label}</ExternalLink>;
          if (a.kind === "pdf") return <ExternalLink key={a.kind} url={a.url} className="ph-btn" data-testid="report-open-pdf">{a.label}</ExternalLink>;
          return <button key={a.kind} type="button" className="ph-btn fill" data-testid={`report-publish-${row.reportNumber}`} disabled={busy} onClick={() => onPublish(row)}>{a.label}</button>;
        })}
      </div>
      {row.pdfFailed && !current ? <span className="ph-small" data-testid="report-pdf-missing" style={{ color: "var(--ph-warn)" }}>PDF not created</span> : null}
      {row.hasHistory ? (
        <>
          <button
            type="button"
            className="ph-btn link"
            style={{ justifyContent: "flex-start", minHeight: 32, padding: 0, fontSize: 13 }}
            aria-expanded={open}
            data-testid={`report-history-toggle-${row.reportNumber}`}
            onClick={() => {
              if (!open) history.load();
              onToggle();
            }}
          >
            {open ? "Hide earlier versions" : `${row.historyCount} earlier version${row.historyCount === 1 ? "" : "s"}`}
          </button>
          {open ? (
            <div className="ph-stack" style={{ gap: 6 }} data-testid={`report-history-${row.reportNumber}`}>
              {history.loading ? <span className="ph-note">Loading…</span> : null}
              {!history.loading && history.links.length === 0 ? <span className="ph-note">{history.failed || !history.canLoad ? "Earlier versions could not be listed." : "No earlier versions."}</span> : null}
              {history.links.map((link) => (
                <div key={link.key} className="ph-versionrow">
                  <span>{link.label} · superseded</span>
                  {link.url ? <a href={link.url} target="_blank" rel="noopener noreferrer">Open read only</a> : <span className="ph-faint">no link</span>}
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

function MissingResultItem({ code, item, ctx }) {
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const row = mergeMissingResultRow(item, ctx.resolveMissingResult ? ctx.resolveMissingResult(code, item.scheduleRef) : null);
  async function remind() {
    if (!ctx.onRemindResult) return;
    setPending(true);
    const outcome = await ctx.onRemindResult({ code, scheduleRef: item.scheduleRef });
    setPending(false);
    setResult(outcome);
  }
  const inspector = row ? (row.assignedTo ? initialsName(row.assignedTo) : "Unassigned") : null;
  return (
    <div className="ph-gap-item" data-testid={`report-missing-row-${code}-${item.scheduleRef}`}>
      <span className="ph-cardtitle" style={{ fontSize: 14 }}>{row ? row.title || "Inspection" : "Inspection"}</span>
      <span className="ph-small">{[row && row.plannedDate ? `Planned ${formatDay(row.plannedDate)}` : null, row && row.category, inspector, !row ? item.scheduleRef : null].filter(Boolean).join(" · ")}</span>
      <div className="ph-two" style={{ marginTop: 6 }}>
        <button type="button" className="ph-btn sec" disabled={pending || !ctx.onRemindResult} onClick={remind}>{pending ? "…" : "Remind inspector"}</button>
        <button
          type="button"
          className="ph-btn"
          disabled={!ctx.onNavigate || !(row && row.plannedDate)}
          onClick={() => ctx.onNavigate("plan", { openItem: { code, scheduleRef: item.scheduleRef, plannedDate: row.plannedDate } })}
        >
          Open in Plan
        </button>
      </div>
      {result ? <span role="status" className={`ph-result${result.ok ? "" : " warn"}`}>{result.message}</span> : null}
    </div>
  );
}

function PhotoItem({ code, item, ctx }) {
  const pa = ctx.pendingAction && ctx.pendingAction.code === code && ctx.pendingAction.inspectionNumber === item.inspectionNumber ? ctx.pendingAction : null;
  const label = `CI-${item.inspectionNumber} ${item.title || ""}`.trim();
  return (
    <div className="ph-gap-item" data-testid={`report-photo-row-${code}-${item.inspectionNumber}`}>
      <span className="ph-cardtitle" style={{ fontSize: 14 }}>{item.title || "Inspection"}</span>
      <span className="ph-small">{[`CI-${item.inspectionNumber}`, item.inspectionDate, item.category, item.inspector && item.inspector.upn ? item.inspector.upn : "no inspector assigned"].filter(Boolean).join(" · ")}</span>
      {pa ? (
        <div className="ph-stack" data-testid={`report-confirm-${code}`} style={{ gap: 8, marginTop: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Confirm: {pa.kind === "remindPhoto" ? `Remind the inspector to add a photo to ${pa.label}` : `Accept ${pa.label} without a photo`}
          </span>
          {pa.kind === "waivePhoto" ? (
            <label className="ph-field">
              Reason to accept without a photo
              <input
                className="ph-input"
                type="text"
                value={pa.reason}
                maxLength={500}
                placeholder="e.g. area closed, photo not permitted"
                aria-label="Reason for accepting without a photo"
                data-testid={`report-reason-${code}`}
                onChange={(e) => ctx.setPendingAction({ ...pa, reason: e.target.value })}
              />
            </label>
          ) : null}
          <div className="ph-two">
            <button type="button" className="ph-btn sec" disabled={pa.kind === "waivePhoto" && !pa.reason.trim()} onClick={ctx.confirmInline}>Confirm</button>
            <button type="button" className="ph-btn" onClick={() => ctx.setPendingAction(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="ph-two" style={{ marginTop: 6 }}>
          <button
            type="button"
            className="ph-btn"
            disabled={!ctx.onRemindPhoto || !(item.inspector && item.inspector.upn)}
            onClick={() => ctx.startInline(code, "remindPhoto", { inspectionNumber: item.inspectionNumber, label })}
          >
            Remind inspector
          </button>
          <button
            type="button"
            className="ph-btn sec"
            disabled={!ctx.onWaivePhoto || !item.etag}
            onClick={() => ctx.startInline(code, "waivePhoto", { inspectionNumber: item.inspectionNumber, etag: item.etag, label })}
          >
            Accept without photo
          </button>
        </div>
      )}
    </div>
  );
}

/** ReportCheck.dc.html: the existing readiness lines, their items and actions. */
function CheckScreen({ ctx, project, view, onClose }) {
  const gaps = itemsToFix(project);
  const what = project.period ? "this period" : "this week";
  const clear = {
    inspection_results: project.period ? "Every booked inspection in this period has a result." : "Every booked inspection this week has a result.",
    inspection_photos: "Every inspection has a photo or an acceptance.",
    ncr_decisions: project.period ? "No NCRs are awaiting a decision in this period." : "No NCRs are awaiting a decision this week.",
  };
  return (
    <PhoneFullScreen
      title="Check the data"
      subtitle={`${project.code} · ${ctx.header.text}`}
      titleId="ph-check-title"
      onBack={onClose}
      backLabel="Back to Weekly report"
      testId="ph-report-check"
      footer={
        <button type="button" className="ph-btn pri" data-testid="ph-check-confirm" disabled={!project.allGreen} onClick={() => { ctx.confirmData(project); onClose(); }}>
          {project.allGreen ? "Confirm data complete" : "Confirm data complete · when all green"}
        </button>
      }
    >
      {gaps > 0 ? (
        <div className="ph-card warnbox" role="status">
          <Svg size={20} stroke={2} style={{ color: "var(--ph-amber)", flexShrink: 0 }}>{ICON.warn}</Svg>
          <span>{gaps} gap{gaps === 1 ? "" : "s"} before you can confirm {what}</span>
        </div>
      ) : (
        <div className="ph-card okbox" role="status"><span style={{ color: "var(--ph-ok-ink)" }}>Every line is clear. You can confirm the data.</span></div>
      )}
      {project.checklist.map((line) => {
        const items = line.items || [];
        const bad = !line.green;
        return (
          <section key={line.id} className="ph-gap" data-testid={`report-line-${project.code}-${line.id}`} data-bad={bad ? "true" : "false"}>
            <div className="ph-gap-h"><span className={`ph-dot${bad ? " warn" : ""}`} /><h2 style={{ fontSize: 15, fontWeight: 600 }}>{bad ? `${LINE_LABEL[line.id] || line.label} · ${line.count}` : LINE_LABEL[line.id] || line.label}</h2></div>
            {!bad ? <span className="ph-note">{clear[line.id] || "Nothing to show."}</span> : null}
            {bad && items.length === 0 ? <span className="ph-note" style={{ color: "var(--ph-warn)" }}>{`${line.count} ${(LINE_LABEL[line.id] || line.id).toLowerCase()} — list not available yet.`}</span> : null}
            {bad && line.id === "inspection_results" && items.length ? <span className="ph-small">The inspector records the result in the capture app. If the inspection did not happen, move or cancel it in Plan.</span> : null}
            {bad && line.id === "inspection_results" ? items.map((item) => <MissingResultItem key={item.scheduleRef} code={project.code} item={item} ctx={ctx} />) : null}
            {bad && line.id === "inspection_photos" ? items.map((item) => <PhotoItem key={item.inspectionNumber} code={project.code} item={item} ctx={ctx} />) : null}
            {bad && line.id === "ncr_decisions"
              ? items.map((item) => (
                  <div key={item.ncrNumber} className="ph-gap-item">
                    <span className="ph-cardtitle" style={{ fontSize: 14 }}>{item.title || `NCR D${item.ncrNumber}`}</span>
                    <button type="button" className="ph-btn sec" onClick={() => ctx.onNavigate && ctx.onNavigate("ncrs", { code: project.code, ncrNumber: item.ncrNumber })}>Open NCR</button>
                  </div>
                ))
              : null}
          </section>
        );
      })}
      {project.unapprovedRecords > 0 ? (
        <section className="ph-gap" data-testid={`report-readiness-unapproved-${project.code}`}>
          <span className="ph-note">{unapprovedRecordsText(project.unapprovedRecords)}</span>
          <a href="#today" data-testid={`report-readiness-unapproved-link-${project.code}`} style={{ color: "var(--ph-acc-text)" }} onClick={(e) => { e.preventDefault(); if (ctx.onNavigate) ctx.onNavigate("endofday"); }}>Go to End of day</a>
        </section>
      ) : null}
      {project.reminders && project.reminders.length ? (
        <section className="ph-gap">
          <span className="ph-note">Older NCRs still open:</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {project.reminders.map((item) => (
              <button key={item.ncrNumber} type="button" className="ph-smallbtn ph-mono" onClick={() => ctx.onNavigate && ctx.onNavigate("ncrs", { code: project.code, ncrNumber: item.ncrNumber })}>D{item.ncrNumber}</button>
            ))}
          </div>
        </section>
      ) : null}
      {view.outcome ? (
        <span role="status" data-testid={`report-outcome-${project.code}`} className={`ph-result${view.outcome.errorCode ? " warn" : ""}`}>
          {view.outcome.errorCode ? outcomeText(view.outcome.errorCode, view.outcome) : STATUS_TEXT[view.outcome.status] || "Done."}
        </span>
      ) : null}
    </PhoneFullScreen>
  );
}

/** ReportPeriod.dc.html: the existing Custom period (From, To) as a bottom sheet. */
function PeriodSheet({ ctx, onClose }) {
  const [from, setFrom] = useState(ctx.periodFrom || (ctx.model.period ? ctx.model.period.periodStart : ""));
  const [to, setTo] = useState(ctx.periodTo || (ctx.model.period ? ctx.model.period.periodEnd : ""));
  const clientMessage = validatePeriodInput({ from, to, today: ctx.today });
  const ready = Boolean(from && to && !clientMessage);
  return (
    <PhoneSheet labelledBy="ph-period-title" onClose={onClose} testId="ph-period-sheet">
      <div className="ph-stack" style={{ gap: 4 }}>
        <h2 id="ph-period-title" className="ph-sheet-title">Custom period</h2>
        <span className="ph-sheet-note">For a report that does not follow a Monday–Sunday week, such as the first report after start-up.</span>
      </div>
      <div className="ph-two" style={{ gap: 10 }}>
        <label className="ph-field">
          From
          <input className="ph-input" type="date" data-testid="report-period-from" value={from} max={ctx.today} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="ph-field">
          To
          <input className="ph-input" type="date" data-testid="report-period-to" value={to} max={ctx.today} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      {clientMessage || ctx.periodServerText ? <span role="alert" data-testid="report-period-error" style={{ color: "var(--ph-bad)", fontSize: 13 }}>{clientMessage || ctx.periodServerText}</span> : null}
      <button
        type="button"
        className="ph-btn pri"
        data-testid="ph-period-use"
        disabled={!ready}
        onClick={() => {
          ctx.setPeriodOpen(true);
          ctx.changePeriod(from, to);
          onClose();
        }}
      >
        Use this period
      </button>
      <button type="button" className="ph-btn link" onClick={() => { ctx.useWeek(); onClose(); }}>Back to weeks</button>
    </PhoneSheet>
  );
}

/** ReportDraft / ReportPdf / ReportPublished: the draft, PDF and publish flow as full screens over the report. */
function FlowScreen({ ctx, project, view, kind, onBack }) {
  const row = view.flowRow || view.row;
  const code = project.code;
  const f = view.flow;
  const title = kind === "published" ? `${f && f.publishedNumber ? `Report ${f.publishedNumber}` : "Report"} · ${code}` : kind.startsWith("pdf") || kind === "publishing" ? "Approve" : reportScreenTitle(ctx.header);
  const subtitle = kind === "published" ? undefined : `${code} · ${kind.startsWith("pdf") || kind === "publishing" ? `${project.periodText}${row ? ` · v${row.displayVersion}` : ""}` : project.periodText}`;
  let footer = null;
  let body = null;
  if (kind === "generating") {
    const step = view.built.steps[2];
    body = (
      <div role="status" className="ph-card statusbox" data-testid={`report-generating-${code}`}>
        <span className="ph-stepno">Step 3 of 6</span>
        <span style={{ fontSize: 18, fontWeight: 700 }}>Generating the draft…</span>
        {step.progress ? <span className="ph-small" style={{ color: "var(--ph-t4)" }}>{step.progress.title}</span> : null}
        <div className="ph-bar run"><span /></div>
        <span style={{ fontSize: 13, color: "var(--ph-acc-text)" }}>{step.progress ? step.progress.detail : ""}</span>
      </div>
    );
  } else if (kind === "draft-ready") {
    body = (
      <div className="ph-card" data-testid={`report-version-created-${code}`}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="ph-stepno">Step 4 of 6</span>
          {row ? <PhoneChip tone="warn" className="ph-chip big warn">{`Draft · v${row.displayVersion}`}</PhoneChip> : null}
        </div>
        <span style={{ fontSize: 18, fontWeight: 700 }}>Draft ready to review</span>
        <span className="ph-note">The new draft is ready. Open it in Word and make any changes; edits are saved straight into the report.</span>
        {ctx.created && ctx.created.code === code && ctx.created.unapproved ? (
          <span className="ph-note" data-testid={`report-unapproved-${code}`}>
            {unapprovedRecordsText(ctx.created.unapproved)}{" "}
            <a href="#today" data-testid={`report-unapproved-link-${code}`} style={{ color: "var(--ph-acc-text)" }} onClick={(e) => { e.preventDefault(); if (ctx.onNavigate) ctx.onNavigate("endofday"); }}>Go to End of day</a>
          </span>
        ) : null}
        {ctx.created && ctx.created.code === code && ctx.created.notes && ctx.created.notes.length ? (
          <ul className="ph-note" data-testid={`report-version-notes-${code}`} style={{ margin: 0, paddingLeft: 18 }}>
            {ctx.created.notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        ) : null}
        {row ? <ExternalLink url={row.wordUrl} className="ph-btn mid" data-testid={`report-open-word-${code}`}>Edit in Word</ExternalLink> : null}
        {row ? (
          <button type="button" className="ph-btn link" disabled={view.flowBusy} data-testid={`report-regenerate-${code}`} onClick={() => ctx.setDialog({ kind: "regenerate", code, reason: "", reportNumber: row.reportNumber, status: row.raw.reportStatus, publishedNumber: row.publishedNumber })}>
            Regenerate draft
          </button>
        ) : null}
      </div>
    );
    footer = row ? <button type="button" className="ph-btn pri" disabled={view.flowBusy} onClick={() => ctx.askApprove(project, row)}>Approve this draft</button> : null;
  } else if (kind === "pdf-making") {
    body = (
      <div role="status" className="ph-card statusbox" data-testid={`report-approving-${code}`}>
        <span style={{ fontSize: 18, fontWeight: 700 }}>Making the PDF…</span>
        {f && f.kind === "approving" && f.version ? <span className="ph-small" style={{ color: "var(--ph-t4)" }}>Approved v{f.version}</span> : null}
        <div className="ph-bar run"><span /></div>
        <span style={{ fontSize: 13, color: "var(--ph-acc-text)" }}>Usually under 30 seconds. The buttons stay off until it is done.</span>
      </div>
    );
    footer = <button type="button" className="ph-btn pri" disabled>Publish PDF</button>;
  } else if (kind === "pdf-ready") {
    body = (
      <>
        <div role="status" className="ph-card okbox" data-testid={`report-pdf-ready-${code}`}>
          <span style={{ color: "var(--ph-ok)" }}><Svg size={24} stroke={2.2}>{ICON.checkCircle}</Svg></span>
          <span className="ph-stack">
            <span style={{ fontSize: 16, fontWeight: 600, color: "var(--ph-ok-ink)" }}>Approved{f && f.version ? ` v${f.version}` : ""} · PDF ready</span>
            <span style={{ fontSize: 13, color: "var(--ph-ok-ink2)" }}>Still internal. Nothing reaches the client until you publish.</span>
            {f && f.fileName ? <span className="ph-mono ph-small" style={{ color: "var(--ph-ok-ink2)" }}>{f.fileName}</span> : null}
          </span>
        </div>
        {row && row.pdfOpenUrl ? <ExternalLink url={row.pdfOpenUrl} className="ph-btn mid">Open PDF</ExternalLink> : null}
      </>
    );
    footer = row && ctx.canPublish ? <button type="button" className="ph-btn pri" disabled={view.flowBusy} onClick={() => ctx.askPublish(project, row)}>Publish PDF</button> : null;
  } else if (kind === "pdf-failed") {
    body = (
      <div role="alert" className="ph-card badbox" data-testid={`report-flow-error-${code}`}>
        <span style={{ fontSize: 16, fontWeight: 600, color: "var(--ph-bad-ink)" }}>PDF not created</span>
        <span style={{ fontSize: 13, color: "var(--ph-bad-ink2)" }}>{f.message}</span>
        {row && ctx.canRetry ? <button type="button" className="ph-btn warn" style={{ minHeight: 48, fontWeight: 700 }} disabled={view.flowBusy} onClick={() => ctx.retryPdf(project, row)}>Retry PDF</button> : null}
      </div>
    );
  } else if (kind === "error") {
    body = (
      <div role="alert" className="ph-card badbox" data-testid={`report-flow-error-${code}`}>
        <span style={{ fontSize: 16, fontWeight: 600, color: "var(--ph-bad-ink)" }}>{f.op === "publish" ? "The approved report was not published" : "The draft was not approved"}</span>
        <span style={{ fontSize: 13, color: "var(--ph-bad-ink2)" }}>{f.message}</span>
        {f.errorCode === "already_published" && f.url ? <a href={f.url} target="_blank" rel="noopener noreferrer">Open the file already on the project site</a> : null}
      </div>
    );
  } else if (kind === "publishing") {
    body = (
      <div role="status" className="ph-card statusbox" data-testid={`report-publishing-${code}`}>
        <span style={{ fontSize: 18, fontWeight: 700 }}>Publishing the approved report…</span>
        <ul className="ph-checks" aria-label="Checks">
          {PUBLISH_CHECKS(code).map((c) => (
            <li key={c}><span>◌</span>{c}</li>
          ))}
        </ul>
        <span style={{ fontSize: 13, color: "var(--ph-acc-text)" }}>Takes a few seconds. Nothing is published unless every check passes.</span>
      </div>
    );
  } else if (kind === "published") {
    const siteUrl = (f && f.siteUrl) || (row && row.publishedUrl) || "";
    body = (
      <>
        <div role="status" className="ph-card okbox" data-testid={`report-published-${code}`}>
          <span style={{ color: "var(--ph-ok)" }}><Svg size={26} stroke={2.2}>{ICON.checkCircle}</Svg></span>
          <span className="ph-stack">
            <span style={{ fontSize: 18, fontWeight: 700, color: "var(--ph-ok-ink)" }}>Published to the client</span>
            <span style={{ fontSize: 13, color: "var(--ph-ok-ink2)" }}>{f.stamp ? `${f.stamp} · the client can open it now` : "The client can open it now"}</span>
          </span>
        </div>
        <dl className="ph-dl" style={{ gridTemplateColumns: "96px 1fr" }}>
          {f.publishedNumber ? (<><dt>Number</dt><dd>Report {f.publishedNumber}</dd></>) : null}
          <dt>Period</dt><dd>{project.periodText}</dd>
          {row && row.pdfOpenUrl ? (<><dt>PDF</dt><dd className="ph-mono" style={{ fontSize: 13 }}>{fileNameOf(row.pdfOpenUrl)}</dd></>) : null}
          {row && fileNameOf(row.wordUrl) ? (<><dt>Word</dt><dd className="ph-mono" style={{ fontSize: 13 }}>{fileNameOf(row.wordUrl)}</dd></>) : null}
        </dl>
        <div className="ph-stack" style={{ gap: 8 }}>
          {siteUrl ? <a className="ph-btn pri" href={siteUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>Open on project site</a> : null}
          {row && row.wordUrl ? <a className="ph-btn mid" href={row.wordUrl} target="_blank" rel="noopener noreferrer">Open in Word (internal)</a> : null}
        </div>
        {row ? <EarlierVersions row={row} onLoadVersions={ctx.onLoadVersions} /> : null}
        <button type="button" className="ph-btn link" onClick={() => { ctx.setFlowFor(code, null); ctx.onNavigate("today"); }}>Back to Today</button>
      </>
    );
  }
  return (
    <PhoneFullScreen title={title} subtitle={subtitle} titleId="ph-flow-title" onBack={onBack} backLabel="Back to Weekly report" testId={`ph-report-flow-${kind}`} footer={footer}>
      {body}
    </PhoneFullScreen>
  );
}

/** ReportPublish.dc.html / the approve confirmation / reopen and regenerate: the existing dialogs as bottom sheets. */
function DialogSheet({ ctx }) {
  const d = ctx.dialog;
  const close = () => ctx.setDialog(null);
  if (d.kind === "approve") {
    const versionLabel = d.version ? ` v${d.version}` : "";
    const when = d.editedAt ? `last edited in Word ${formatClock(d.editedAt)}` : d.generatedAt ? `generated ${formatClock(d.generatedAt)}` : "not edited since it was generated";
    return (
      <PhoneSheet labelledBy="ph-dlg-title" onClose={close} testId="report-dialog-approve">
        <h2 id="ph-dlg-title" className="ph-sheet-title">Approve the draft{versionLabel}?</h2>
        <p className="ph-sheet-note">Period {d.periodText} · {when}. Approving locks this version and makes a PDF of exactly what you see in Word.</p>
        <ul className="ph-checks" aria-label="Checks">
          <li>{d.confirmedAt ? `✓ Data confirmed ${formatDayTime(d.confirmedAt)}` : "✓ Data confirmed"}</li>
          <li>• Checked now: no unresolved review markers</li>
          <li>• Locks this version until you reopen it</li>
        </ul>
        <button type="button" className="ph-btn pri" data-testid="report-dialog-approve-confirm" onClick={() => ctx.doApprove(d)}>Approve{versionLabel}</button>
        <button type="button" className="ph-btn link" data-autofocus="true" onClick={close}>Cancel</button>
      </PhoneSheet>
    );
  }
  if (d.kind === "publish") {
    return (
      <PhoneSheet labelledBy="ph-dlg-title" onClose={close} testId="report-dialog-publish">
        <h2 id="ph-dlg-title" className="ph-sheet-title">Publish the approved report to the client?</h2>
        <dl className="ph-dl" style={{ gridTemplateColumns: "96px 1fr" }}>
          <dt>File</dt><dd className="ph-mono" style={{ fontSize: 13 }}>{d.fileName || "Approved report (PDF)"}</dd>
          <dt>Where</dt><dd>{d.code} project site · Weekly Reports</dd>
          <dt>Who sees it</dt><dd>{d.whoSees || `Members of the ${d.code} project site`}</dd>
        </dl>
        <p className="ph-note">The client gets the PDF only. The Word file stays internal.</p>
        <ul className="ph-checks" aria-label="Checks">
          {PUBLISH_CHECKS(d.code).map((c) => (
            <li key={c}><span>•</span>{c}</li>
          ))}
        </ul>
        <button type="button" className="ph-btn pri" data-testid="report-dialog-publish-confirm" onClick={() => ctx.doPublish(d)}>Publish PDF</button>
        <button type="button" className="ph-btn link" data-autofocus="true" onClick={close}>Cancel</button>
      </PhoneSheet>
    );
  }
  const isReopen = d.kind === "reopen";
  return (
    <PhoneSheet labelledBy="ph-dlg-title" onClose={close} testId={`report-dialog-${d.kind}`}>
      <h2 id="ph-dlg-title" className="ph-sheet-title">{isReopen ? `Reopen ${reportRef(d.status, d.publishedNumber)} as a draft?` : `Regenerate ${reportRef(d.status, d.publishedNumber)}?`}</h2>
      <p className="ph-sheet-note">
        {isReopen
          ? "The report goes back to a draft you can edit in Word. The PDF made from it is replaced when you approve again."
          : "Builds a new Word draft from the confirmed data. Your Word edits stay in the version history."}
      </p>
      <textarea
        className="ph-textarea"
        rows={3}
        data-autofocus="true"
        value={d.reason}
        placeholder="Reason (required)"
        aria-label="Reason"
        data-testid={`report-reason-${d.code}`}
        onChange={(e) => ctx.setDialog({ ...d, reason: e.target.value })}
      />
      <button
        type="button"
        className="ph-btn pri"
        data-testid={`report-dialog-${d.kind}-confirm`}
        disabled={!d.reason.trim()}
        onClick={() => {
          if (isReopen) ctx.doReopen(d, d.reason);
          else {
            ctx.setDialog(null);
            const project = ctx.model.projects.find((p) => p.code === d.code);
            if (project) ctx.generate(project, { reason: d.reason.trim() });
          }
        }}
      >
        {isReopen ? "Reopen as draft" : "Regenerate"}
      </button>
      <button type="button" className="ph-btn link" onClick={close}>Cancel</button>
    </PhoneSheet>
  );
}

/**
 * The weekly report on a phone (Report.dc.html and its screens). Everything it
 * does is the desktop screen's own state and handlers (`ctx`, built in
 * Report.jsx): the period control, the six-step model (one primary action for
 * the current step), the table rows as cards with the same actions, readiness
 * (Check the data), the approve / PDF / publish flow and its dialogs.
 */
export default function PhoneReport({ ctx }) {
  const { model, header } = ctx;
  const [checkFor, setCheckFor] = useState(ctx.initialPhoneCheck || null); // project code
  const [periodSheet, setPeriodSheet] = useState(false);
  const [dismissed, setDismissed] = useState({}); // code -> flow-screen signature the user went back from
  const customOnScreen = Boolean(model.period);
  const periodPending = ctx.periodOpen && !customOnScreen;
  const projects = periodPending ? [] : model.projects;

  const views = projects.map((p) => ({ project: p, view: ctx.projectView(p) }));
  const checkTarget = checkFor ? views.find((v) => v.project.code === checkFor) : null;
  // The one full-screen flow view a project has open right now (draft, PDF, publish), unless dismissed.
  const flowTarget = views
    .map((v) => {
      const createdHere = Boolean(ctx.created && ctx.created.code === v.project.code && !(v.view.outcome && v.view.outcome.errorCode));
      const kind = flowScreenKind({ generating: Boolean(v.view.gening), flow: v.view.flow, created: createdHere });
      return { ...v, kind, sig: flowScreenSignature(kind, { flow: v.view.flow, generation: v.view.gening }) };
    })
    .find((v) => v.kind && dismissed[v.project.code] !== v.sig);

  function runPrimary(project, view, action) {
    const row = view.row;
    if (action === "check") setCheckFor(project.code);
    else if (action === "confirm") ctx.confirmData(project);
    else if (action === "generate") ctx.generate(project);
    else if (action === "approve" && row) ctx.askApprove(project, row);
    else if (action === "retry" && row) ctx.retryPdf(project, row);
    else if (action === "publish" && row) ctx.askPublish(project, row);
  }
  function leaveFlow(target) {
    setDismissed((prev) => ({ ...prev, [target.project.code]: target.sig }));
    const code = target.project.code;
    if (target.view.flow && ["pdf_ready", "error", "published"].includes(target.view.flow.kind)) ctx.setFlowFor(code, null);
    if (target.kind === "draft-ready") ctx.setCreated(null);
  }

  return (
    <div className="ph-screen" data-testid="phone-report">
      <header className="ph-head rep">
        <h1 className="ph-h1">Weekly report</h1>
        <div className="ph-weeknav" data-testid="report-period-control">
          {!header.custom && !ctx.periodOpen && ctx.onPrevWeek ? (
            <button type="button" className="ph-iconbtn" data-testid="report-prev-week" aria-label={`Previous week, ${header.prevLabel.replace("‹ ", "")}`} disabled={ctx.weekNavLoading} onClick={ctx.onPrevWeek}><Svg size={16}>{ICON.prev}</Svg></button>
          ) : null}
          <span className="ph-weeklabel" data-testid="report-header-title" aria-live="polite">{periodPending ? "Custom period" : header.text}</span>
          {!header.custom && !ctx.periodOpen && ctx.onNextWeek && !header.atCurrent ? (
            <button type="button" className="ph-iconbtn" data-testid="report-next-week" aria-label={`Next week, ${header.nextLabel.replace(" ›", "")}`} disabled={ctx.weekNavLoading} onClick={ctx.onNextWeek}><Svg size={16}>{ICON.next}</Svg></button>
          ) : null}
          {ctx.showPeriodControl ? (
            <button type="button" className={`ph-smallbtn${customOnScreen || ctx.periodOpen ? " on" : ""}`} data-testid="report-custom-period" aria-pressed={customOnScreen || ctx.periodOpen} style={{ height: 44 }} onClick={() => setPeriodSheet(true)}>Custom</button>
          ) : null}
        </div>
        {!header.custom && !ctx.periodOpen && !header.atCurrent && ctx.onBackToCurrent ? (
          <button type="button" className="ph-btn link" style={{ minHeight: 32, justifyContent: "flex-start", padding: 0, fontSize: 13 }} data-testid="report-back-to-current" disabled={ctx.weekNavLoading} onClick={ctx.onBackToCurrent}>Back to this week</button>
        ) : null}
      </header>

      <main className="ph-main">
        {ctx.periodMessage ? <span role="alert" data-testid="report-period-error" style={{ color: "var(--ph-bad)", fontSize: 13 }}>{ctx.periodMessage}</span> : null}
        {periodPending && !ctx.periodMessage ? <span data-testid="report-period-hint" className="ph-note">Choose a From and a To date to see readiness for that period.</span> : null}
        {model.errors.length > 0 ? (
          <div data-testid="report-errors" className="ph-card badbox">
            {model.errors.map((e) => (
              <span key={e.code} style={{ color: "var(--ph-bad-ink)" }}><strong>{e.code}</strong> Unavailable &mdash; {e.error}</span>
            ))}
          </div>
        ) : null}

        {views.map(({ project, view }) => {
          const card = view.hasSteps ? nextStepCard(view.built, { row: view.row, itemsToFix: itemsToFix(project), period: Boolean(project.period) }) : null;
          const generateBlocked = card && card.primary && card.primary.action === "generate" && !view.local && Boolean(project.generationElsewhere || view.localAny);
          const primaryLocked = Boolean(view.flowBusy || generateBlocked || (view.currentFlow && view.currentFlow.kind === "retrying"));
          return (
            <section key={project.code} className="ph-section" data-testid={`report-project-${project.code}`} aria-labelledby={`ph-p-${project.code}`} style={{ gap: 14 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <h2 id={`ph-p-${project.code}`} className="ph-mono" style={{ fontSize: 17, fontWeight: 600 }}>{project.code}</h2>
                <span className="ph-small" data-testid={`report-summary-${project.code}`}>{view.hasSteps ? view.built.summary : ""}</span>
              </div>
              <span className="ph-small">Report for {project.periodText}</span>
              {view.outcome ? (
                <span role="status" data-testid={`report-outcome-${project.code}`} className={`ph-result${view.outcome.errorCode ? " warn" : ""}`}>
                  {view.outcome.errorCode
                    ? `${outcomeText(view.outcome.errorCode, view.outcome)}${view.outcome.lines ? " " + view.outcome.lines.map((l) => `${l.id}: ${l.count}`).join(", ") : ""}${view.outcome.jobId ? ` (job ${view.outcome.jobId})` : ""}`
                    : STATUS_TEXT[view.outcome.status] || "Done."}
                </span>
              ) : null}
              {card ? (
                <NextStepCard card={card} busy={primaryLocked} onAction={(action) => runPrimary(project, view, action)} />
              ) : project.mode === "covered" && project.coveredBy ? (
                <div className="ph-card" data-testid={`report-covered-${project.code}`}>
                  <span className="ph-cardtitle">{project.coveredBy.label}</span>
                  <span className="ph-mono ph-small">{project.coveredBy.coversTag}</span>
                </div>
              ) : (
                <div className="ph-card" data-testid={`report-legacy-${project.code}`}><span className="ph-note">No status recorded on this report &mdash; read-only. Open it, and every version, from the list below.</span></div>
              )}

              <section className="ph-section">
                <h2 className="ph-label">Reports</h2>
                {project.archive.length === 0 ? (
                  <div className="ph-card"><span className="ph-note">No reports yet</span></div>
                ) : (
                  project.archive.map((row) => (
                    <ReportCard
                      key={row.key}
                      row={row}
                      current={project.current ? row.reportNumber === project.current.reportNumber : false}
                      busy={view.flowBusy}
                      canPublish={Boolean(ctx.canPublish)}
                      canRetry={Boolean(ctx.canRetry)}
                      onApprove={(r) => ctx.askApprove(project, r)}
                      onRetry={(r) => ctx.retryPdf(project, r)}
                      onPublish={(r) => ctx.askPublish(project, r)}
                      open={Boolean(ctx.openHistory[`${project.code}-${row.reportNumber}`])}
                      onToggle={() => ctx.onToggleHistory(`${project.code}-${row.reportNumber}`)}
                      onLoadVersions={ctx.onLoadVersions}
                    />
                  ))
                )}
              </section>
              {view.step && view.step.key === "review" && view.row ? (
                <button type="button" className="ph-btn link" disabled={view.flowBusy} data-testid={`report-regenerate-${project.code}`} style={{ justifyContent: "flex-start", padding: 0, minHeight: 32, fontSize: 13 }} onClick={() => ctx.setDialog({ kind: "regenerate", code: project.code, reason: "", reportNumber: view.row.reportNumber, status: view.row.raw.reportStatus, publishedNumber: view.row.publishedNumber })}>
                  Regenerate…
                </button>
              ) : null}
              {view.step && view.step.key === "publish" && view.row && !view.flowBusy ? (
                <button type="button" className="ph-btn link" data-testid={`report-reopen-${project.code}`} style={{ justifyContent: "flex-start", padding: 0, minHeight: 32, fontSize: 13 }} onClick={() => ctx.setDialog({ kind: "reopen", code: project.code, reason: "", reportNumber: view.row.reportNumber, status: view.row.raw.reportStatus, publishedNumber: view.row.publishedNumber, etag: view.row.etag })}>
                  Reopen as draft…
                </button>
              ) : null}
            </section>
          );
        })}
      </main>

      {checkTarget ? <CheckScreen ctx={ctx} project={checkTarget.project} view={checkTarget.view} onClose={() => setCheckFor(null)} /> : null}
      {flowTarget ? <FlowScreen ctx={ctx} project={flowTarget.project} view={flowTarget.view} kind={flowTarget.kind} onBack={() => leaveFlow(flowTarget)} /> : null}
      {periodSheet ? <PeriodSheet ctx={ctx} onClose={() => setPeriodSheet(false)} /> : null}
      {ctx.dialog ? <DialogSheet ctx={ctx} /> : null}
    </div>
  );
}
